import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';

export interface BusinessRuleItem {
  question: string;
  answer: string;
  badge?: string;
  type?: 'rule' | 'warning' | 'tip';
}

export interface FaqTroubleshootItem {
  id: string;
  question: string;
  category: string;
  summary: string;
  explanation: string;
  verificationSteps: string[];
  moduleKey?: string;
  tags: string[];
}

export interface ActionShortcutItem {
  trigger: string;
  description: string;
  scope?: string;
}

export interface ModuleGuide {
  routeKey: string;
  title: string;
  subtitle: string;
  icon: string;
  targetAudience: string;
  overview: string;
  rules: BusinessRuleItem[];
  shortcuts: ActionShortcutItem[];
  tags: string[];
}

@Injectable({
  providedIn: 'root'
})
export class BusinessGuideService {
  private readonly router = inject(Router);

  // ── Signals de Estado ──
  readonly isOpen = signal<boolean>(false);
  readonly activeTab = signal<'context' | 'faq' | 'all-modules'>('context');
  readonly activeSearch = signal<string>('');
  readonly selectedModuleKey = signal<string>('home');
  readonly selectedFaqCategory = signal<string>('all');

  // ── Base de FAQ & Dúvidas Frequentes WFM Madero ──
  readonly faqList: FaqTroubleshootItem[] = [
    {
      id: 'faq-clt-interjornada',
      question: 'Por que o sistema bloqueia ou alerta um turno com menos de 11h de descanso?',
      category: 'Conformidade Trabalhista CLT',
      summary: 'O Art. 66 da CLT exige intervalo mínimo de 11 horas consecutivas entre duas jornadas de trabalho.',
      explanation: 'Se um colaborador encerra o turno de fechamento às 23:00, ele só poderá iniciar o próximo turno a partir das 10:00 do dia seguinte. O validador em tempo real aponta infração de Interjornada caso esse limite seja violado e oferece o botão "Corrigir Automaticamente (1-Click)" para ajustar o turno imediatamente.',
      verificationSteps: [
        '1. Abra o modal de edição de turno clicando na célula do colaborador na grade semanal.',
        '2. Confira o horário de saída do dia anterior e o cálculo exato exibido no Assistente CLT.',
        '3. Clique em "Corrigir Automaticamente / Aplicar Horário Sugerido" para ajustar com 1 clique ou informe justificativa operacional se autorizado.'
      ],
      moduleKey: 'schedule-planning',
      tags: ['clt', 'interjornada', '11h', 'descanso', 'alerta', 'bloqueio', 'turno', 'sugestao']
    },
    {
      id: 'faq-sunday-rule',
      question: 'Como funciona a regra do Domingo Mensal (Lei 10.101/2000) e a Semana de 36h?',
      category: 'Conformidade Trabalhista CLT',
      summary: 'Todo colaborador deve usufruir de pelo menos 1 folga ao domingo a cada 4 semanas (máximo de 3 domingos trabalhados seguidos).',
      explanation: 'No comércio e restaurantes (Lei 10.101/2000 c/c Art. 67 da CLT), o descanso semanal remunerado (DSR) deve coincidir com o domingo ao menos 1 vez no período máximo de 4 semanas. Além disso, na semana em que o colaborador usufrui a folga dominical de referência, a parametrização Madero aplica a trava de Carga Semanal Reduzida (36h) para equilíbrio do banco de horas.',
      verificationSteps: [
        '1. Na grade de Planejamento de Escala, verifique os indicadores da coluna Dom (D1, D2, D3 ou Folga).',
        '2. Caso o colaborador atinja 3 domingos trabalhados consecutivos, o 4º domingo exigirá Folga Dominical obrigatória.',
        '3. Use o botão "Aplicar Folga Sugerida (1-Click)" no modal de turno para converter o dia em DSR/Folga automaticamente.'
      ],
      moduleKey: 'schedule-planning',
      tags: ['domingo', 'mensal', 'dsr', 'folga', '36h', 'lei 10101', 'clt']
    },
    {
      id: 'faq-18-rules',
      question: 'Quais são os 18 campos da Regra de Escala por Filial?',
      category: 'Operações & Regras',
      summary: 'Contemplam parâmetros trabalhistas (CLT) e travas de negócio específicas por marca (STH, CTN, JER).',
      explanation: 'Cada operação vincula um template de regra contendo limites de dias consecutivos (6), horas sem intervalo (6h), intrajornada (1h-2h), interjornada (11h), carga semanal (44h / 36h dom), pré-abertura/pós-fechamento de Cozinha e Salão, Liderança Mínima Presencial e Cobertura Mínima.',
      verificationSteps: [
        '1. Acesse a tela "Operações & Regras de Escala" e selecione a aba "Regras de Escala (17 Parâmetros)".',
        '2. Edite qualquer parâmetro na tela para atualizar o template de regra.',
        '3. Vincule a regra desejada às filiais na aba "Lista de Operações".'
      ],
      moduleKey: 'branches',
      tags: ['regras', '18 campos', 'filial', 'abertura', 'fechamento', 'lideranca', 'parametrizacao']
    },
    {
      id: 'faq-timecard-tolerance',
      question: 'Como funciona a tolerância de 10 minutos no Ponto (Art. 58 §1º CLT)?',
      category: 'Ponto & Aderência',
      summary: 'Variações de até 5 minutos por batida, limitado a 10 minutos diários na soma total, não geram desconto nem hora extra (Paycode 001).',
      explanation: 'Pelo Art. 58 §1º da CLT, se a soma das variações do dia ficar em até ±10 minutos, o registro é 100% aderente (Paycode 001 - Horas Normais). Porém, se ultrapassar 10 minutos no dia (ex: +14 min ou -12 min), a jurisprudência trabalhista (Súmula 366 TST) determina o cômputo da totalidade dos minutos excedentes como Hora Extra (Paycode 050) ou Atraso/Saída Antecipada (Paycode 102).',
      verificationSteps: [
        '1. Acesse "Aderência Escala x Ponto & Correções" e filtre por restaurante e período.',
        '2. Confira a coluna de variação em minutos e o Paycode gerado automaticamente pelo motor.',
        '3. Em caso de esquecimento de batida ou troca operacional autorizada, clique em "Solicitar Correção" para abrir um chamado TK-xxxxxx para a Equipe de Ponto.'
      ],
      moduleKey: 'timecard',
      tags: ['ponto', 'aderencia', 'art 58', '10 minutos', 'tolerancia', 'ticket', 'chamado', 'correcao', 'paycode']
    },
    {
      id: 'faq-rm-vacations',
      question: 'Por que um funcionário não recebe turnos na geração automática?',
      category: 'Funcionários & Férias',
      summary: 'Colaboradores em Férias ou Afastados no RM ficam bloqueados automaticamente no período.',
      explanation: 'O sistema cruza a Situação Real do colaborador e a tabela de Controle de Férias/Afastamentos importada do TOTVS RM. Durante o período de gozo de férias ou licença médica, o algoritmo bloqueia a alocação de turnos.',
      verificationSteps: [
        '1. Acesse "Funcionários, Jornadas & Férias" na aba "Controle de Férias e Afastamentos".',
        '2. Verifique as datas de início e fim registradas para a matrícula (Chapa).',
        '3. Confirme também na aba "Diagnóstico de Correlação" se a função e filial do colaborador estão mapeadas.'
      ],
      moduleKey: 'employees',
      tags: ['ferias', 'afastado', 'rm', 'bloqueio', 'colaborador', 'chapa']
    }
  ];

  // ── Catálogo Completo por Módulo (Todas as Telas WFM) ──
  readonly catalog: Record<string, ModuleGuide> = {
    'home': {
      routeKey: 'home',
      title: 'Visão Geral da Operação',
      subtitle: 'Resumo operacional de cobertura semanal, eficiência da escala e alertas das filiais',
      icon: 'dashboard',
      targetAudience: 'Diretores, Gerentes Regionais (GO/OP), Gestores e Admin',
      overview: 'Apresenta os indicadores principais do Workforce Management da rede Madero: operações ativas, colaboradores escalados, escalas publicadas, eficiência média de cobertura (Dom a Sáb) e alertas de férias e aviso prévio.',
      rules: [
        {
          question: 'Filtro Hierárquico por Diretor, Gerente Regional (GO/OP) e Filial',
          answer: 'Selecione qualquer Diretor de Operações ou Gerente Regional para recalcular instantaneamente os KPIs, a Matriz de Cobertura Semanal (Dom a Sáb) e os alertas das unidades sob aquela gestão.',
          badge: 'Visão Operacional',
          type: 'rule'
        },
        {
          question: 'Matriz de Cobertura Semanal (Dom a Sáb)',
          answer: 'Exibe a relação entre o quadro escalado e a demanda prevista em cada dia da semana por operação. Células verdes indicam cobertura ideal (>= 92%), âmbar atenção (80% a 91%) e vermelhas déficit crítico (< 80%).',
          badge: 'Cobertura',
          type: 'tip'
        }
      ],
      shortcuts: [
        { trigger: '⌘K / Ctrl+K', description: 'Abrir Command Palette Spotlight' },
        { trigger: 'F1', description: 'Abrir Central de Regras WFM & FAQ' },
        { trigger: '/', description: 'Focar na busca rápida da página' }
      ],
      tags: ['home', 'dashboard', 'kpi', 'cobertura', 'diretor']
    },
    'schedule-planning': {
      routeKey: 'schedule-planning',
      title: 'Planejamento de Escalas',
      subtitle: 'Grade semanal e diária por restaurante com geração assistida 6x1, validação CLT e sugestão 1-Click',
      icon: 'calendar_month',
      targetAudience: 'Gestores de Restaurante e Administradores',
      overview: 'Permite aos gestores gerar o rascunho da escala com o motor 6x1 e ajustar manualmente os turnos da equipe em visão semanal ou diária, contando com explicações diretas das regras da CLT e botão de correção automática em 1 clique.',
      rules: [
        {
          question: 'Assistente CLT e Sugestão Inteligente 1-Click',
          answer: 'Ao editar um turno com conflito trabalhista (ex: colaborador saiu às 23:00 ontem e precisa de 11h de interjornada até as 10:00 de hoje), o sistema explica exatamente o motivo e exibe o botão "Corrigir Automaticamente" para aplicar o horário em conformidade com 1 clique.',
          badge: 'Sugestão 1-Click',
          type: 'tip'
        },
        {
          question: 'Regra do Domingo Mensal (Lei 10.101/2000) e Semana 36h',
          answer: 'Nenhum colaborador pode trabalhar mais de 3 domingos consecutivos (mínimo de 1 folga dominical a cada 4 semanas). Na semana com folga ao domingo de referência, o limite contratual passa a 36h semanais.',
          badge: 'Compliance CLT',
          type: 'warning'
        },
        {
          question: 'Justificativa Obrigatória e Trilha de Auditoria',
          answer: 'Qualquer edição manual em uma escala exige o preenchimento de justificativa operacional, registrando o usuário, data/hora e valores anteriores/novos no Log de Auditoria.',
          badge: 'Auditoria',
          type: 'rule'
        }
      ],
      shortcuts: [
        { trigger: 'F1', description: 'Ver regras trabalhistas desta tela' },
        { trigger: 'ESC', description: 'Fechar modal de edição de turno' }
      ],
      tags: ['escala', 'manual', 'turno', 'clt', 'interjornada', 'grade', 'domingo']
    },
    'branches': {
      routeKey: 'branches',
      title: 'Operações, Mapeamento de Diretoria/GO & Regras de Escala',
      subtitle: 'Gestão de unidades BI/Protheus, alteração em lote de hierarquia e 17 parâmetros de escala',
      icon: 'storefront',
      targetAudience: 'Administradores WFM e Diretoria de Operações',
      overview: 'Centraliza o cadastro das operações do Grupo Madero (Steakhouse, Container, Jeronimo), o mapeamento em lote de Diretores e Gerentes Regionais (GO/OP) e os 17 parâmetros de regras de escala.',
      rules: [
        {
          question: 'Mapeamento em Lote de Diretor e Gerente Regional (GO/OP)',
          answer: 'Permite atualizar o código e nome de um Diretor (ex: DR.01) ou Gerente Regional (ex: T01) e propagar automaticamente a alteração para todas as filiais vinculadas sem edição individual.',
          badge: 'Atualização em Lote',
          type: 'tip'
        },
        {
          question: 'Os 18 Campos de Parametrização de Regra de Escala',
          answer: 'Controlam Max Dias Consecutivos, Max Horas s/ Intervalo, Intervalo Mín/Máx, Interjornada (11h), Carga Semanal (44h) e c/ Folga Dom (36h), horas de Abertura/Fechamento Cozinha e Salão, Liderança Mínima Presencial, Staff de Abertura por marca, Evitar Troca em Pico e Cobertura Mínima.',
          badge: '17 Parâmetros',
          type: 'rule'
        }
      ],
      shortcuts: [
        { trigger: '/', description: 'Buscar operação por nome BI, Protheus ou cidade' }
      ],
      tags: ['operacoes', 'filiais', 'diretor', 'go', 'regras', '18 campos']
    },
    'employees': {
      routeKey: 'employees',
      title: 'Funcionários, Jornadas/Funções & Controle de Férias',
      subtitle: 'Cadastro de colaboradores por Chapa, mapeamento 6x1/5x2, funções Cozinha/Salão e férias RM',
      icon: 'badge',
      targetAudience: 'Administradores, RH e Gestores',
      overview: 'Gerencia o quadro de colaboradores disponíveis para escala, seus modelos de contrato (6x1 - 07:20, 5x2 - 08:48, 12x36), o mapeamento de funções (Base vs Liderança, Cozinha vs Salão) e períodos de férias.',
      rules: [
        {
          question: 'Classificação de Função: Nível (Base/Liderança) e Setor (Cozinha/Salão)',
          answer: 'Fundamental para o Plano de Chão: garante que o algoritmo cumpra a Liderança Mínima Presencial por turno e jamais aloque um profissional de Cozinha para cobrir demanda de Salão.',
          badge: 'Plano de Chão',
          type: 'rule'
        },
        {
          question: 'Bloqueio Automático por Férias e Afastamentos (Integração RM)',
          answer: 'Colaboradores com período de férias vigente ou status Afastado ficam automaticamente protegidos contra alocação na escala manual e automática.',
          badge: 'Proteção RM',
          type: 'warning'
        }
      ],
      shortcuts: [
        { trigger: '/', description: 'Buscar funcionário por Chapa, Nome ou Função' }
      ],
      tags: ['funcionarios', 'colaboradores', 'jornada', '6x1', 'ferias', 'funcoes']
    },
    'demand-history': {
      routeKey: 'demand-history',
      title: 'Histórico de Pedidos, Feriados & Correlação de Dados',
      subtitle: 'Curvas de demanda hora a hora, calendário de feriados com peso de impacto e diagnóstico cadastral',
      icon: 'Query_Stats',
      targetAudience: 'Administradores e Planejamento Operacional',
      overview: 'Alimenta o motor preditivo com o volume histórico de pedidos por hora de cada operação, aplica multiplicadores de feriados nacionais/estaduais/municipais e audita a integridade entre as bases.',
      rules: [
        {
          question: 'Fator de Impacto em Feriados e Eventos Locais',
          answer: 'Permite configurar o peso percentual (ex: +35% em Feriado Nacional de Shopping ou -20% em centro comercial corporativo) para ajustar automaticamente a demanda projetada.',
          badge: 'Sazonalidade',
          type: 'tip'
        },
        {
          question: 'Diagnóstico de Correlação Cadastral',
          answer: 'Verifica se todas as filiais possuem regra de escala, colaboradores ativos, líderes suficientes e histórico de pedidos válido antes da geração das escalas.',
          badge: 'Integridade de Dados',
          type: 'rule'
        }
      ],
      shortcuts: [
        { trigger: 'F1', description: 'Abrir guia de importação de demanda' }
      ],
      tags: ['pedidos', 'demanda', 'feriados', 'correlacao', 'importacao']
    },
    'timecard': {
      routeKey: 'timecard',
      title: 'Aderência Escala x Ponto & Solicitações de Correção',
      subtitle: 'Confronto Previsto x Realizado (Art. 58 CLT) e fila de tickets TK-xxxxxx para Equipe de Ponto',
      icon: 'punch_clock',
      targetAudience: 'Gestores de Restaurante, Equipe de Ponto e Administradores',
      overview: 'Monitora em tempo real a aderência entre os turnos planejados na escala e as marcações reais de ponto eletrônico, integrando o fluxo de abertura e tratamento de chamados de ajuste.',
      rules: [
        {
          question: 'Cálculo de Aderência e Tolerância CLT (Art. 58)',
          answer: 'Variações de até 5 minutos por batida (máximo 10 min no dia) são consideradas 100% aderentes. Desvios superiores sinalizam Atraso, Hora Extra, Interjornada violada ou Falta.',
          badge: 'Art. 58 CLT',
          type: 'rule'
        },
        {
          question: 'Fila de Chamados de Correção de Ponto (TK-xxxxxx)',
          answer: 'Gestores abrem solicitações anexando justificativa (ex: esquecimento de batida, troca autorizada). A Equipe de Ponto aprova ou recusa com parecer registrado em auditoria.',
          badge: 'Workflow SLA',
          type: 'tip'
        }
      ],
      shortcuts: [
        { trigger: '/', description: 'Filtrar batidas ou número do ticket TK-xxxxxx' }
      ],
      tags: ['ponto', 'aderencia', 'chamados', 'tickets', 'correcao', 'atraso']
    },
    'reports': {
      routeKey: 'reports',
      title: 'Central de Relatórios Gerenciais & Operacionais',
      subtitle: 'Exportação CSV/PDF de Escalas Planejadas, Aderência de Ponto, Absenteísmo, HE e Auditoria',
      icon: 'assessment',
      targetAudience: 'Diretoria, Gestores, RH, Ponto e Administradores',
      overview: 'Disponibiliza os relatórios oficiais do WFM Madero e permite criar variações customizadas com filtros por Diretoria, Regional, Filial, Setor e Período.',
      rules: [
        {
          question: 'Os 4 Relatórios Oficiais + Variações Customizadas',
          answer: 'Inclui: 1) Escalas Planejadas e Cobertura HHT, 2) Aderência entre Escala e Ponto, 3) Indicadores de Solicitações de Correção de Ponto (abertos, pendentes, fechados) e 4) Auditoria/Log de Alterações.',
          badge: 'Exportação Oficial',
          type: 'rule'
        }
      ],
      shortcuts: [
        { trigger: 'F1', description: 'Ver formatos de exportação suportados' }
      ],
      tags: ['relatorios', 'exportacao', 'csv', 'pdf', 'absenteismo', 'horas extras']
    },
    'admin': {
      routeKey: 'admin',
      title: 'Administração de Acessos & Log de Auditoria',
      subtitle: 'Gestão de perfis (Admin, Gestor, Equipe de Ponto), vínculo de filiais e Rollback de alterações',
      icon: 'admin_panel_settings',
      targetAudience: 'Administradores do Sistema',
      overview: 'Controla as permissões de acesso por perfil e unidade operacional, além de registrar 100% das alterações relevantes realizadas no sistema com opção de reversão (Rollback).',
      rules: [
        {
          question: 'Perfis de Acesso WFM Madero',
          answer: 'Gestor de Restaurante: visualiza e edita apenas sua unidade e abre tickets de ponto. Equipe de Ponto: trata solicitações TK-xxxxxx. Administrador: acesso total às parametrizações, importações e auditoria.',
          badge: 'RBAC & Alçadas',
          type: 'rule'
        },
        {
          question: 'Rastreabilidade Completa e Reversão (Rollback)',
          answer: 'Toda alteração de escala, parâmetro de filial ou aprovação de ponto grava quem alterou, quando e o snapshot anterior, permitindo reverter a ação com 1 clique.',
          badge: 'Rollback Seguro',
          type: 'warning'
        }
      ],
      shortcuts: [
        { trigger: '/', description: 'Buscar no log de auditoria por gestor ou funcionário' }
      ],
      tags: ['admin', 'usuarios', 'perfis', 'auditoria', 'logs', 'rollback']
    }
  };

  readonly moduleKeys = Object.keys(this.catalog);

  readonly faqCategories = computed(() => {
    const cats = new Set(this.faqList.map(f => f.category));
    return ['all', ...Array.from(cats)];
  });

  readonly currentModule = computed(() => {
    return this.catalog[this.selectedModuleKey()] || this.catalog['home'];
  });

  readonly filteredFaq = computed(() => {
    const q = this.activeSearch().trim().toLowerCase();
    const cat = this.selectedFaqCategory();

    return this.faqList.filter(item => {
      const matchCat = cat === 'all' || item.category === cat;
      if (!matchCat) return false;
      if (!q) return true;

      return (
        item.question.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.explanation.toLowerCase().includes(q) ||
        item.tags.some(t => t.toLowerCase().includes(q)) ||
        item.verificationSteps.some(s => s.toLowerCase().includes(q))
      );
    });
  });

  readonly filteredRules = computed(() => {
    const mod = this.currentModule();
    if (!mod) return [];

    const q = this.activeSearch().trim().toLowerCase();
    if (!q) return mod.rules;

    return mod.rules.filter(r =>
      r.question.toLowerCase().includes(q) ||
      r.answer.toLowerCase().includes(q) ||
      (r.badge && r.badge.toLowerCase().includes(q))
    );
  });

  openCurrentRouteGuide(): void {
    const currentPath = this.resolveCurrentRouteKey();
    this.selectedModuleKey.set(currentPath);
    this.activeTab.set('context');
    this.isOpen.set(true);
  }

  openFaqTab(): void {
    this.activeTab.set('faq');
    this.isOpen.set(true);
  }

  openGuide(moduleKey?: string): void {
    if (moduleKey && this.catalog[moduleKey]) {
      this.selectedModuleKey.set(moduleKey);
    } else {
      this.selectedModuleKey.set(this.resolveCurrentRouteKey());
    }
    this.activeTab.set('context');
    this.isOpen.set(true);
  }

  openForRoute(routePath: string): void {
    const cleanKey = (routePath || '').replace(/^\//, '').split('/')[0] || 'home';
    this.openGuide(cleanKey);
  }

  closeGuide(): void {
    this.isOpen.set(false);
    this.activeSearch.set('');
  }

  toggleGuide(): void {
    if (this.isOpen()) {
      this.closeGuide();
    } else {
      this.openCurrentRouteGuide();
    }
  }

  private resolveCurrentRouteKey(): string {
    const url = this.router.url.split('?')[0].split('#')[0];
    const segment = url.split('/')[1] || 'home';
    return this.catalog[segment] ? segment : 'home';
  }
}
